import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Table, Form, Button, Alert, Spinner, Modal, Row, Col, Card, Badge,
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaExternalLinkAlt, FaUserPlus, FaBuilding, FaUsers, FaEnvelope } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { api } from '../../../contexts/AuthContext';

const ACCENT = '#60A5FA';

const emptyCompany = () => ({
  name: '',
  description: '',
  address: '',
  city: '',
  postal_code: '',
  country: '',
  phone_number: '',
  email: '',
  website: '',
  vat_number: '',
  tax_office: '',
});

const emptyContact = () => ({
  first_name: '',
  last_name: '',
  position: '',
  phone_number: '',
  email: '',
});

const blankToNull = (v) => {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
};

const CompanyModal = ({ show, onHide, onSubmit, initialData }) => {
  const [form, setForm] = useState(emptyCompany());

  useEffect(() => {
    if (!show) return;
    if (initialData) {
      setForm({
        name: initialData.name || '',
        description: initialData.description || '',
        address: initialData.address || '',
        city: initialData.city || '',
        postal_code: initialData.postal_code || '',
        country: initialData.country || '',
        phone_number: initialData.phone_number || initialData.phone || '',
        email: initialData.email || '',
        website: initialData.website || '',
        vat_number: initialData.vat_number || '',
        tax_office: initialData.tax_office || '',
      });
    } else {
      setForm(emptyCompany());
    }
  }, [show, initialData]);

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name: form.name.trim(),
      description: blankToNull(form.description),
      address: blankToNull(form.address),
      city: blankToNull(form.city),
      postal_code: blankToNull(form.postal_code),
      country: blankToNull(form.country),
      phone_number: blankToNull(form.phone_number),
      email: blankToNull(form.email),
      website: blankToNull(form.website),
      vat_number: blankToNull(form.vat_number),
      tax_office: blankToNull(form.tax_office),
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered contentClassName="bg-dark text-light">
      <Modal.Header closeButton closeVariant="white" style={{ borderBottom: '1px solid #333' }}>
        <Modal.Title>{initialData ? 'Edit company' : 'Add company'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className="bg-dark text-light border-secondary"
                  placeholder="Legal / display name"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>VAT number</Form.Label>
                <Form.Control
                  value={form.vat_number}
                  onChange={(e) => set('vat_number', e.target.value)}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  value={form.address}
                  onChange={(e) => set('address', e.target.value)}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>City</Form.Label>
                <Form.Control
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Postal code</Form.Label>
                <Form.Control
                  value={form.postal_code}
                  onChange={(e) => set('postal_code', e.target.value)}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Country</Form.Label>
                <Form.Control
                  value={form.country}
                  onChange={(e) => set('country', e.target.value)}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Tax office</Form.Label>
                <Form.Control
                  value={form.tax_office}
                  onChange={(e) => set('tax_office', e.target.value)}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  value={form.phone_number}
                  onChange={(e) => set('phone_number', e.target.value)}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-1">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  value={form.website}
                  onChange={(e) => set('website', e.target.value)}
                  placeholder="https://..."
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #333' }}>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button type="submit" style={{ backgroundColor: ACCENT, borderColor: ACCENT }}>
            {initialData ? 'Save' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const ContactModal = ({ show, onHide, onSubmit, companyName, initialData }) => {
  const [form, setForm] = useState(emptyContact());

  useEffect(() => {
    if (!show) return;
    if (initialData) {
      setForm({
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        position: initialData.position || '',
        phone_number: initialData.phone_number || '',
        email: initialData.email || '',
      });
    } else {
      setForm(emptyContact());
    }
  }, [show, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      position: form.position.trim(),
      phone_number: blankToNull(form.phone_number),
      email: blankToNull(form.email),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered contentClassName="bg-dark text-light">
      <Modal.Header closeButton closeVariant="white" style={{ borderBottom: '1px solid #333' }}>
        <Modal.Title>
          {initialData ? 'Edit contact' : 'Add contact'}
          {companyName ? ` · ${companyName}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>First name</Form.Label>
                <Form.Control
                  required
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Last name</Form.Label>
                <Form.Control
                  required
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Position</Form.Label>
            <Form.Control
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
              placeholder="e.g. Owner, Accountant"
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  value={form.phone_number}
                  onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-1">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #333' }}>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button type="submit" style={{ backgroundColor: ACCENT, borderColor: ACCENT }}>
            {initialData ? 'Save' : 'Add contact'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const AllCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactCompany, setContactCompany] = useState(null);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/api/companies/');
      setCompanies(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load companies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const countries = useMemo(() => {
    const set = new Set();
    companies.forEach((c) => {
      if (c.country) set.add(c.country);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [companies]);

  const stats = useMemo(() => {
    const withContacts = companies.filter((c) => (c.contact_count || c.contact_persons?.length || 0) > 0).length;
    const missingEmail = companies.filter((c) => !c.email).length;
    const withVat = companies.filter((c) => c.vat_number).length;
    return {
      total: companies.length,
      withContacts,
      missingEmail,
      withVat,
    };
  }, [companies]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return companies
      .filter((c) => !countryFilter || c.country === countryFilter)
      .filter((c) => {
        if (!q) return true;
        const hay = [
          c.name,
          c.city,
          c.country,
          c.email,
          c.vat_number,
          c.phone_number,
          c.description,
          ...(c.contact_persons || []).map((p) => `${p.first_name} ${p.last_name}`),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
  }, [companies, search, countryFilter]);

  const saveCompany = async (data) => {
    try {
      if (editingCompany) {
        await api.put(`/api/companies/${editingCompany.id}/`, data);
        toast.success('Company updated');
      } else {
        await api.post('/api/companies/', data);
        toast.success('Company created');
      }
      setShowCompanyModal(false);
      setEditingCompany(null);
      await fetchCompanies();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.name?.[0] || 'Failed to save company';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save company');
    }
  };

  const deleteCompany = async (company) => {
    if (!window.confirm(`Delete “${company.name}” and its contacts?`)) return;
    try {
      await api.delete(`/api/companies/${company.id}/`);
      toast.success('Deleted');
      await fetchCompanies();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const saveContact = async (data) => {
    if (!contactCompany) return;
    try {
      await api.post('/api/contact-persons/', { ...data, company: contactCompany.id });
      toast.success('Contact added');
      setShowContactModal(false);
      setContactCompany(null);
      await fetchCompanies();
    } catch (err) {
      toast.error('Failed to add contact');
    }
  };

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 style={{ color: ACCENT, marginBottom: 4 }}>Companies</h2>
          <div style={{ color: '#9ca3af', fontSize: 13 }}>
            Clients & partners · contacts · VAT & billing details
          </div>
        </div>
        <Button
          style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
          onClick={() => {
            setEditingCompany(null);
            setShowCompanyModal(true);
          }}
        >
          <FaPlus className="me-2" /> Add company
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <Row className="mb-3 g-3">
            <Col md={3} sm={6}>
              <Card className="bg-dark text-light border-secondary h-100">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#9ca3af', fontSize: 13 }}>
                    <FaBuilding style={{ color: ACCENT }} /> Companies
                  </div>
                  <h3 className="mb-0">{stats.total}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="bg-dark text-light border-secondary h-100">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#9ca3af', fontSize: 13 }}>
                    <FaUsers style={{ color: '#22c55e' }} /> With contacts
                  </div>
                  <h3 className="mb-0 text-success">{stats.withContacts}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="bg-dark text-light border-secondary h-100">
                <Card.Body>
                  <div style={{ color: '#9ca3af', fontSize: 13 }} className="mb-1">With VAT</div>
                  <h3 className="mb-0">{stats.withVat}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="bg-dark text-light border-secondary h-100">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#9ca3af', fontSize: 13 }}>
                    <FaEnvelope style={{ color: '#fbbf24' }} /> Missing email
                  </div>
                  <h3 className="mb-0" style={{ color: stats.missingEmail ? '#fbbf24' : '#fff' }}>
                    {stats.missingEmail}
                  </h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="bg-dark text-light border-secondary">
            <Card.Header className="bg-dark border-secondary d-flex flex-wrap gap-2">
              <Form.Control
                type="search"
                placeholder="Search name, contact, VAT, city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-dark text-light border-secondary"
                style={{ width: 280 }}
              />
              <Form.Select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="bg-dark text-light border-secondary"
                style={{ width: 'auto' }}
              >
                <option value="">All countries</option>
                {countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Form.Select>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover variant="dark" responsive className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Location</th>
                    <th>Contact</th>
                    <th>People</th>
                    <th>VAT</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-4" style={{ color: '#9ca3af' }}>
                        No companies yet. Add your first client or partner.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((company) => {
                      const contacts = company.contact_persons || [];
                      const count = company.contact_count ?? contacts.length;
                      return (
                        <tr key={company.id}>
                          <td>
                            <Link
                              to={`/company/${company.id}`}
                              style={{ color: ACCENT, fontWeight: 600, textDecoration: 'none' }}
                            >
                              {company.name}
                            </Link>
                            {company.website ? (
                              <a
                                href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                                target="_blank"
                                rel="noreferrer"
                                className="ms-2"
                                style={{ color: '#9ca3af' }}
                                title="Website"
                              >
                                <FaExternalLinkAlt size={11} />
                              </a>
                            ) : null}
                            {company.description ? (
                              <div>
                                <small style={{ color: '#9ca3af' }}>
                                  {company.description.length > 80
                                    ? `${company.description.slice(0, 80)}…`
                                    : company.description}
                                </small>
                              </div>
                            ) : null}
                          </td>
                          <td style={{ color: '#d1d5db' }}>
                            {company.formatted_address ||
                              [company.city, company.country].filter(Boolean).join(', ') ||
                              '—'}
                          </td>
                          <td>
                            <div style={{ color: '#d1d5db' }}>
                              {company.phone_number || company.phone || '—'}
                            </div>
                            <small style={{ color: '#9ca3af' }}>{company.email || 'No email'}</small>
                          </td>
                          <td>
                            {count > 0 ? (
                              <>
                                <Badge bg="secondary" className="me-2">{count}</Badge>
                                <small style={{ color: '#9ca3af' }}>
                                  {contacts
                                    .slice(0, 2)
                                    .map((p) => `${p.first_name} ${p.last_name}`)
                                    .join(', ')}
                                  {contacts.length > 2 ? '…' : ''}
                                </small>
                              </>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>None</span>
                            )}
                          </td>
                          <td style={{ color: '#d1d5db' }}>{company.vat_number || '—'}</td>
                          <td className="text-end">
                            <div className="d-flex gap-1 justify-content-end flex-wrap">
                              <Button
                                size="sm"
                                variant="outline-success"
                                title="Add contact"
                                onClick={() => {
                                  setContactCompany(company);
                                  setShowContactModal(true);
                                }}
                              >
                                <FaUserPlus />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                style={{ borderColor: ACCENT, color: ACCENT }}
                                onClick={() => {
                                  setEditingCompany(company);
                                  setShowCompanyModal(true);
                                }}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => deleteCompany(company)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}

      <CompanyModal
        show={showCompanyModal}
        onHide={() => {
          setShowCompanyModal(false);
          setEditingCompany(null);
        }}
        onSubmit={saveCompany}
        initialData={editingCompany}
      />

      <ContactModal
        show={showContactModal}
        onHide={() => {
          setShowContactModal(false);
          setContactCompany(null);
        }}
        onSubmit={saveContact}
        companyName={contactCompany?.name}
      />
    </Container>
  );
};

export default AllCompanies;
